import { useEffect } from 'react'
import { useQueryClient } from 'react-query'
import { HubConnectionBuilder, HttpTransportType, LogLevel } from '@microsoft/signalr'
import { API_ORIGIN } from '../utils/constants/url'
import localStorageIds from '../utils/constants/localStorageIds'
import QueryKeys from '../utils/constants/queryKeys'

export const STREAM_STATE_CHANGED = 'StreamStateChanged'
export const STREAM_FEATURES_CHANGED = 'StreamFeaturesChanged'
export const COMMENTARY_MODE_CHANGED = 'CommentaryModeChanged'
export const AD_VIDEO_CHANGED = 'AdVideoChanged'
export const AD_QUEUE_CHANGED = 'AdQueueChanged'

export const adVideoKey = (streamId) => [QueryKeys.GET_AD_VIDEO, streamId]
export const adQueueKey = (streamId) => [QueryKeys.GET_AD_QUEUE, streamId]

/**
 * Keeps the stream's cached state, autofollow flag and commentary mode (GET_LIVESTREAM), the ad
 * video on air (GET_AD_VIDEO) and what plays next (GET_AD_QUEUE) current over the stream's live hub:
 * the backend pushes each change as it saves it. Pushes are best effort, so all three are refetched on
 * reconnect, and the page refetches them on window focus.
 */
const useStreamState = (orgId, streamId, enabled) => {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled || !orgId || !streamId) return undefined
    const token = localStorage.getItem(localStorageIds.TOKEN)
    if (!token) return undefined
    const key = [QueryKeys.GET_LIVESTREAM, streamId]
    const adKey = adVideoKey(streamId)
    const queueKey = adQueueKey(streamId)

    // Straight over a websocket, as the scoreboard hub does: the API's replicas share no
    // negotiated connection state, so the request that opens the socket must be the one that
    // registers it.
    const connection = new HubConnectionBuilder()
      .withUrl(
        `${API_ORIGIN}/v1/orgs/${orgId}/streams/${streamId}/live` +
          `?access_token=${encodeURIComponent(token)}`,
        { skipNegotiation: true, transport: HttpTransportType.WebSockets },
      )
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build()

    // Handlers return nothing: the server sends no invocation id, and SignalR logs an error for
    // any value handed back.
    connection.on(STREAM_STATE_CHANGED, ({ state }) => {
      queryClient.setQueryData(key, (current) => ({ ...current, state }))
    })
    connection.on(STREAM_FEATURES_CHANGED, ({ autofollowEnabled }) => {
      queryClient.setQueryData(key, (current) => ({ ...current, autofollowEnabled }))
    })
    connection.on(COMMENTARY_MODE_CHANGED, ({ mode }) => {
      queryClient.setQueryData(key, (current) => ({ ...current, commentaryMode: mode }))
    })
    connection.on(AD_VIDEO_CHANGED, (next) => {
      queryClient.setQueryData(adKey, next ?? null)
    })
    connection.on(AD_QUEUE_CHANGED, (queue) => {
      queryClient.setQueryData(queueKey, queue)
    })
    connection.onreconnected(() => {
      queryClient.invalidateQueries(key)
      queryClient.invalidateQueries(adKey)
      queryClient.invalidateQueries(queueKey)
    })
    connection.start().catch(() => {
      // The reads on focus and reconnect keep the page honest without the push.
    })

    return () => {
      connection.stop()
    }
  }, [enabled, orgId, streamId, queryClient])
}

export default useStreamState
